import { useCallback } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../api/auth/useAuth";
import { isPaymentPastDue } from "../api/services/pricing-plan.billing";
import {
  usePaymentInfo,
  useRetryFailedPayment,
} from "../api/services/pricing-plan.hooks";
import { trackPaymentRetryRequested } from "../analytics/amplitude";

type RetrySource = "banner" | "subscription_card" | "card_change";

/**
 * Failed-payment grace state for the signed-in user (docs/payment-grace-period-design.md),
 * plus a "retry now" action with translated feedback.
 *
 * Copy rule: never tell the user how many days are left; say the plan "will be cancelled soon".
 */
export function usePaymentPastDue() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { token } = useAuth();
  const { data: paymentInfo } = usePaymentInfo({ enabled: Boolean(token) });
  const retryMutation = useRetryFailedPayment();

  const isPastDue = Boolean(token) && isPaymentPastDue(paymentInfo);
  const planName = paymentInfo?.current_plan?.name ?? "";

  const retry = useCallback(
    async (source: RetrySource, { silent = false } = {}) => {
      try {
        await retryMutation.mutateAsync();
        trackPaymentRetryRequested({ source, outcome: "paid" });
        message.success(t("pages.subscriptions.pastDue.retrySuccess"));
        return true;
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        trackPaymentRetryRequested({
          source,
          outcome: status === 400 ? "failed" : "error",
        });
        if (!silent || status === 400) {
          message.error(
            status === 400
              ? t("pages.subscriptions.pastDue.retryFailed")
              : t("pages.subscriptions.pastDue.retryError"),
          );
        }
        return false;
      }
    },
    [retryMutation, message, t],
  );

  return {
    isPastDue,
    planName,
    billing: paymentInfo?.billing ?? null,
    retry,
    isRetrying: retryMutation.isPending,
  };
}
